<template>
    <div
        class="tw:relative"
        :class="[
            isExpanded ? 'expanded' : '',
            props.naked
                ? ''
                : props.flat
                    ? 'tw:bg-transparent tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-200'
                    : 'tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-200',
            props.customClass
        ]"
        :data-cy="props.dataCy"
    >
        <div v-if="props.title || $slots['header-action']" class="tw:flex tw:justify-between tw:items-end tw:px-5 tw:py-4 tw:border-b tw:border-gray-100">
            <template v-if="props.skeleton">
                <div class="tw:flex tw:flex-col tw:gap-1">
                    <div class="tw:h-4 tw:w-28 tw:bg-gray-200 tw:rounded"></div>
                    <div class="tw:h-3 tw:w-44 tw:bg-gray-200 tw:rounded"></div>
                </div>
            </template>
            <template v-else>
                <div>
                    <h2 class="tw:text-sm tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wide tw:my-1">{{ props.title }}</h2>
                    <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:my-1">{{ props.subtitle }}</p>
                </div>
            </template>
            <slot name="header-action"></slot>
        </div>
        <div
            class="tw:w-full tw:overflow-x-auto"
            :style="bodyStyle"
        >
            <table class="tw:w-full tw:text-sm tw:border-collapse" style="table-layout: fixed;">
                <colgroup>
                    <col v-for="col in props.columns" :key="col.key" :class="col.class" :style="colHasWidth(col) ? '' : `width: ${props.defaultColWidth}`" />
                </colgroup>
                <!-- Skeleton mode -->
                <template v-if="props.skeleton">
                    <thead class="tw:text-gray-400 tw:text-xs tw:uppercase tw:tracking-wider tw:animate-pulse">
                        <tr class="tw:border-b tw:border-gray-100">
                            <th v-for="col in props.columns" :key="col.key" :class="['tw:px-5 tw:py-3 tw:font-semibold', col.centered ? 'tw:text-center' : 'tw:text-left', col.class]">
                                <div :class="col.centered ? 'tw:flex tw:justify-center' : ''">
                                    <slot :name="`skeleton-header-${col.key}`" :column="col">
                                        <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-16"></div>
                                    </slot>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody class="tw:animate-pulse">
                        <tr v-for="n in skeletonRowCount" :key="'skeleton-' + n" class="tw:border-b tw:border-gray-100">
                            <td v-for="col in props.columns" :key="col.key" :class="['tw:px-5 tw:py-4', col.class]">
                                <div :class="col.centered ? 'tw:flex tw:justify-center' : ''">
                                    <slot :name="`skeleton-${col.key}`" :column="col">
                                        <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-3/4"></div>
                                    </slot>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </template>

                <!-- Normal mode -->
                <template v-else>
                    <thead class="tw:text-gray-400 tw:text-xs tw:uppercase tw:tracking-wider">
                        <tr class="tw:border-b tw:border-gray-100">
                            <th v-for="col in props.columns" :key="col.key" :class="['tw:px-5 tw:py-3 tw:font-semibold', col.centered ? 'tw:text-center' : 'tw:text-left', col.class]">
                                <div :class="col.centered ? 'tw:flex tw:justify-center' : ''">
                                    {{ col.label }}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody class="tw:divide-y tw:divide-gray-100">
                        <tr
                            v-for="(row, index) in props.data"
                            :key="row[props.rowKey]"
                            class="hover:tw:bg-gray-50 tw:transition-colors"
                            :class="props.clickableRows ? 'tw:cursor-pointer' : ''"
                            @click="onRowClick(row, index)"
                        >
                            <td v-for="col in props.columns" :key="col.key" :class="['tw:px-5 tw:py-4 tw:overflow-hidden', col.truncate ? 'tw:truncate' : '', col.class]">
                                <div :class="col.centered ? 'tw:flex tw:justify-center' : ''">
                                    <slot
                                        :name="`cell-${col.key}`"
                                        :row="row"
                                        :index="index"
                                        :value="getValue(row, col.field || col.key)"
                                    >
                                        <span v-if="col.truncate" :title="getValue(row, col.field || col.key)">{{ getValue(row, col.field || col.key) }}</span>
                                        <template v-else>{{ getValue(row, col.field || col.key) }}</template>
                                    </slot>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="!props.loading && props.data.length === 0">
                            <td :colspan="props.columns.length" class="tw:px-5 tw:py-10 tw:text-center tw:text-gray-400">
                                {{ props.emptyText }}
                            </td>
                        </tr>
                    </tbody>
                </template>
            </table>
        </div>
    </div>
</template>

<script setup>
    import { computed } from 'vue'

    const props = defineProps({
        title: String,
        subtitle: String,
        columns: {
            type: Array,
            required: true
        },
        data: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        skeleton: {
            type: Boolean,
            default: false
        },
        skeletonRows: {
            type: Number,
            default: 5
        },
        rowKey: {
            type: String,
            default: 'id'
        },
        emptyText: {
            type: String,
            default: 'No hay resultados'
        },
        clickableRows: {
            type: Boolean,
            default: false
        },
        customClass: {
            type: String,
            default: ''
        },
        dataCy: {
            type: String,
            default: ''
        },
        flat: {
            type: Boolean,
            default: false
        },
        naked: {
            type: Boolean,
            default: false
        },
        defaultColWidth: {
            type: String,
            default: '100px'
        },
        maxBodyHeight: {
            type: String,
            default: ''
        }
    })

    const emit = defineEmits(['row-click'])

    /**
     * `onRowClick` is exposed as a named function so Vue can resolve the
     * `@click` handler at template-compile time. Using the inline
     * `emit('row-click', ...)` statement used to compile to an
     * `undefined` return value and produced a Vue warn in the parent
     * ("Invalid handler for event 'click': got undefined").
     */
    function onRowClick(row, index) {
        emit('row-click', { row, index })
    }

    const colHasWidth = col => /\btw:w-/.test(col.class || '')

    const bodyStyle = computed(() => {
        if (!props.maxBodyHeight) return {}
        return {
            maxHeight: props.maxBodyHeight,
            overflowY: 'auto'
        }
    })

    const skeletonRowCount = computed(() => {
        if (props.skeletonRows > 0) return props.skeletonRows
        if (props.data.length > 0) return props.data.length
        return 5
    })

    const getValue = (row, field) => {
        if (!field) return ''

        return field.split('.').reduce((obj, key) => obj?.[key], row)
    }

    const isExpanded = computed(() => props.data.length >= 1 && props.data.length <= 3)
</script>
